import {
  useCallback,
  useMemo,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import { getAppLink, openApp } from '@dynatrace-sdk/navigation';

/**
 * @internal
 * useAppLink hook that returns the click function needed for an AppLink.
 */
export function useAppLink({
  appId,
  disabled,
  pageToken,
}: {
  appId: string;
  disabled?: boolean;
  pageToken?: string;
}): {
  /** OnClick handler to be used for AppLinks */
  onClick: (event: ReactMouseEvent<HTMLElement, MouseEvent>) => void;
  /** App link href */
  href: string;
} {
  const href = useMemo(
    () => getAppLink(appId, pageToken as string), // temporarily workaround for type of pageToken should be fixed https://dev-jira.dynatrace.org/browse/UIP-2887
    [appId, pageToken],
  );

  const onClick = useCallback(
    (event: ReactMouseEvent<HTMLElement, MouseEvent>) => {
      const shouldBrowserHandleClick =
        event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey;
      if (!disabled && shouldBrowserHandleClick) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (disabled) {
        return;
      }
      openApp(appId, pageToken as string); // temporarily workaround for type of pageToken should be fixed https://dev-jira.dynatrace.org/browse/UIP-2887
    },
    [appId, disabled, pageToken],
  );

  return {
    onClick,
    href,
  };
}
